import { useLocalStorage } from '@/hooks/useLocalStorage';
import { jwtDecode } from 'jwt-decode';

function useAuthToken() {
  const { storedValue, setValue, removeValue } = useLocalStorage<string | null>(
    'access_token',
    null,
  );

  const isTokenExpired = (token: string) => {
    const decoded = decodeToken(token);

    if (!decoded || typeof decoded !== 'object' || !('exp' in decoded)) {
      console.warn('Token does not have an exp field or is invalid');
      return true;
    }

    const exp = (decoded as { exp: number }).exp;
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds

    return exp < currentTime;
  };

  const decodeToken = (token: string) => {
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error('Invalid token', error);
      return null;
    }
  };

  const userIdFromToken = (token: string) => {
    const userPayload = decodeToken(token);

    if (!userPayload) {
      throw new Error('Invalid token');
    }

    const userId = (userPayload as UserJWTPayload).user_id;
    return userId;
  };

  const userFromToken = (token: string | null) => {
    if (!token) {
      throw new Error('Token required');
    }
    const userPayload = decodeToken(token);

    if (!userPayload) {
      throw new Error('Invalid token');
    }

    return userPayload as UserJWTPayload;
  };

  return {
    token: storedValue,
    setToken: setValue,
    clearToken: removeValue,
    decodeToken,
    isTokenExpired,
    userIdFromToken,
    userFromToken,
  };
}

export { useAuthToken };
